/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";
import Router from "next/router";

import { useShoppingCart } from "@/context/ShoppingCartContext";
import { useLoader } from "@/hooks/utilHooks";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document } from "@contentful/rich-text-types";
import { Button, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import { DeviceMobile } from "@phosphor-icons/react";

import { styles } from "./styles";
import { FearuredRedeemPartnerCardProps } from "./types";
import { LinkUtils } from "@/utils/linkUtils";
import { sanitizeText } from "@/utils/string";

export const FearuredRedeemPartnerCard: React.FC<FearuredRedeemPartnerCardProps> = ({ card, isInformative }) => {
  const { addItemToCart } = useShoppingCart();
  const { hideLoader, loading, showLoader } = useLoader();

  return (
    <Card
      onClick={() => {
        if (card.isInformative) {
          if (card.url || card.buttonUrl) {
            LinkUtils.openLink(card.url || card.buttonUrl);
          }
        } else if (card.category?.id && card.partner?.id && card.productId) {
          Router.push(`/categoria/${card.category.id}/parceiro/${card.partner.id}/produto/${card.productId}`);
        } else if (card.url) {
          LinkUtils.openLink(card.url);
        }
      }}
      sx={{
        flexDirection: "column",
        minHeight: { xs: "531px", md: "350px" },
        cursor: "pointer",
        borderRadius: "16px",
        height: "100%",
        position: "relative",
        backgroundImage: { xs: `url(${card.image || card.featuredImage})`, md: `url(${card.featuredImage || card.image})` },
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden"
      }}
    >
      <CardContent
        sx={{
          p: "0px !important",
          height: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}
      >
        <Stack css={styles.tag} direction="row" mt={{ xs: "16px", md: "24px" }} ml={{ xs: "16px", md: "24px" }}>
          {card.tag?.icon ? <Image src={card.tag.icon} alt="" width={21} height={21} /> : null}
          {card.tag?.title ? <Typography css={styles.partner}>{card.tag.title}</Typography> : null}
        </Stack>

        <Stack
          justifyContent="space-between"
          sx={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(14px)",
            px: { xs: "24px", md: "41px" },
            pt: { xs: "12px", md: "16px" },
            pb: { xs: "24px", md: "16px" },
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px"
          }}
          direction={{
            sm: "column",
            md: "row"
          }}
        >
          <Stack alignSelf="flex-end">
            <Typography css={styles.description}>
              {typeof card.title === "object" ? documentToReactComponents(card.title as Document) : card.title}
            </Typography>

            <Typography css={styles.points}>{card.points} pontos KMV</Typography>

            {card.expiration ? (
              <Typography css={styles.validity}>
                <Typography css={styles.validity_title} component="span">
                  {"Validade: "}
                </Typography>

                {card.expiration}
              </Typography>
            ) : null}
          </Stack>

          {isInformative ? (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              gap={2}
              sx={{
                mt: {
                  xs: "20px",
                  md: "inherit"
                }
              }}
            >
              <DeviceMobile size={50} color="#00ADFF" />

              <Stack>
                <Typography fontWeight={600} color="white">
                  Confira o APP
                </Typography>
                <Typography color="white">e aproveite essa oferta!</Typography>
              </Stack>
            </Stack>
          ) : (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                try {
                  const sanitizedButtonTitle = sanitizeText("Resgatar voucher");
                  const itemTitle = card.title;
                  const sanitizedItemTitle = typeof itemTitle === "string" ? sanitizeText(itemTitle) : "";
                  const sanitizedPartnerName = sanitizeText(card.partner?.name);

                  if ((window as any).dataLayer) {
                    (window as any).dataLayer.push({
                      event: "cta_parceiro",
                      detail: `${sanitizedButtonTitle}:${sanitizedItemTitle}`,
                      description: `parceiro:${sanitizedPartnerName}`,
                      parceiro: sanitizedPartnerName
                    });
                    console.log(`Evento disparado: ${sanitizedButtonTitle}:${sanitizedItemTitle}`);
                  }
                } catch (error) {
                  console.error("Erro ao enviar o evento para o Google Tag Manager", error);
                }

                if (loading) {
                  return;
                }

                showLoader();
                addItemToCart({
                  categoryId: card.category?.id,
                  partnerId: card.partner?.id,
                  productId: card.productId
                }).finally(hideLoader);
              }}
              size="medium"
              variant="contained"
              css={styles.button}
              fullWidth={false}
              sx={{
                mt: {
                  xs: "20px",
                  md: "inherit"
                }
              }}
            >
              {loading ? <CircularProgress size="small" sx={{ width: 24, height: 24, mr: 2, color: "#FFFFFF" }} /> : null}
              Resgatar voucher
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
